import baseApi from 'shared/api/baseApi';

export const EXTERNAL_LINK_AGENT_MODES = ['sales', 'mortgage', 'financial_advisor'] as const;
export type ExternalLinkAgentMode = (typeof EXTERNAL_LINK_AGENT_MODES)[number];

export const EXTERNAL_LINK_PROVIDERS = [
  { id: 'credibid', label: 'Credibid' },
  { id: 'bancame', label: 'Banca.me' },
] as const;

export type ExternalLinkProviderId = (typeof EXTERNAL_LINK_PROVIDERS)[number]['id'];

export const externalLinkProviderLabel = (provider: string): string =>
  EXTERNAL_LINK_PROVIDERS.find((item) => item.id === provider)?.label ?? provider;

export interface ExternalLinkConfig {
  id: number;
  projectId: number;
  provider: string;
  enabled: boolean;
  agentModes: ExternalLinkAgentMode[];
  providerConfig: Record<string, unknown>;
}

export interface ExternalLinkConfigInput {
  provider: string;
  enabled: boolean;
  agentModes: ExternalLinkAgentMode[];
  providerConfig: Record<string, unknown>;
}

const externalLinkConfigsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getExternalLinkConfigs: build.query<ExternalLinkConfig[], number>({
      query: (projectId) => ({
        url: `/backoffice/projects/${projectId}/external-link-configs`,
        method: 'GET',
      }),
      providesTags: (result, _error, projectId) => [
        { type: 'ExternalLinkConfig', id: `LIST-${projectId}` },
        ...(result ?? []).map((config) => ({ type: 'ExternalLinkConfig' as const, id: config.id })),
      ],
    }),
    createExternalLinkConfig: build.mutation<ExternalLinkConfig, { projectId: number; body: ExternalLinkConfigInput }>({
      query: ({ projectId, body }) => ({
        url: `/backoffice/projects/${projectId}/external-link-configs`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'ExternalLinkConfig', id: `LIST-${projectId}` }],
    }),
    updateExternalLinkConfig: build.mutation<
      ExternalLinkConfig,
      { projectId: number; id: number; body: ExternalLinkConfigInput }
    >({
      query: ({ projectId, id, body }) => ({
        url: `/backoffice/projects/${projectId}/external-link-configs/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { projectId, id }) => [
        { type: 'ExternalLinkConfig', id: `LIST-${projectId}` },
        { type: 'ExternalLinkConfig', id },
      ],
    }),
    deleteExternalLinkConfig: build.mutation<void, { projectId: number; id: number }>({
      query: ({ projectId, id }) => ({
        url: `/backoffice/projects/${projectId}/external-link-configs/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { projectId }) => [{ type: 'ExternalLinkConfig', id: `LIST-${projectId}` }],
    }),
  }),
});

export const {
  useGetExternalLinkConfigsQuery,
  useCreateExternalLinkConfigMutation,
  useUpdateExternalLinkConfigMutation,
  useDeleteExternalLinkConfigMutation,
} = externalLinkConfigsApi;
